// @ts-check
import React, { useCallback } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Box, VStack, HStack, Button } from "@chakra-ui/react";
import { ChevronRightIcon, ChevronLeftIcon } from "@chakra-ui/icons";
import { NumberParam, withDefault, useQueryParam } from "use-query-params";
import EmptyState from "components/EmptyState";
import { TRANSACTION_HISTORY_LIMIT } from "api/invoicing";
import useGetTransactionsWithFilters from "api/invoicing/useGetTransactionsWithFilters";

import {
  TransactionListGroupedByDay,
  TransactionListSkeletons,
  EmptyStateTransactions,
} from "../components/TransactionList";
import TransactionFiltersSection from "../components/TransactionFiltersSection";
import { INITIAL_COMPLETED_STATUSES, reduceSelectedFilters } from "../utils";

const CurrentPageParam = withDefault(NumberParam, 0);

/**
 * CompletedTab
 * @description
 * Fetches and displays a list of completed transactions for the transaction history flow
 * @param {object} props
 * @param {string} props.accountId account ID of the user to retrieve transaction history data.
 * @param {object} props.filters Object with filters for query params
 * @param {Array<SelectableTransactionFilter>} props.filters.types Array of UI state of Transaction type filters
 * @param {function} props.toggleTxnType toggle UI state of given transaction type
 * @param {boolean} props.expandFilters state for expanding filter buttons
 * @param {function} props.onClickFilterButton click handler for filter button
 * @returns {JSX.Element}
 */
export default function CompletedTab({
  accountId,
  expandFilters,
  onClickFilterButton,
  filters: { types },
  toggleTxnType,
}) {
  const { formatMessage } = useIntl();
  const [currentPage, setCurrentPage] = useQueryParam("page", CurrentPageParam);

  const { data, isPending: isLoading, isError, error } = useGetTransactionsWithFilters(accountId, {
    types: reduceSelectedFilters(types),
    statuses: INITIAL_COMPLETED_STATUSES,
    page: currentPage,
    limit: TRANSACTION_HISTORY_LIMIT,
  });

  const goToPreviousPage = useCallback(() => {
    setCurrentPage((page) => {
      if (page > 0) {
        return page - 1;
      }
    });
  }, [setCurrentPage]);

  const goToNextPage = useCallback(async () => {
    setCurrentPage((page) => {
      return page + 1;
    });
  }, [setCurrentPage]);

  const transactions = data?.payload?.transactions || [];
  const totalTransactions = data?.payload?.total || 0;
  const totalPages =
    totalTransactions > 0 ? Math.ceil(totalTransactions / TRANSACTION_HISTORY_LIMIT) : 1;
  const hasPreviousPage = currentPage > 0;
  const hasNextPage = currentPage + 1 < totalPages;
  const pageInfo = formatMessage(
    {
      defaultMessage: "Page {currentPage} of {totalPages}",
      description: "Example: Page 1 of 10",
    },
    {
      currentPage: currentPage + 1,
      totalPages,
    }
  );

  const renderEmptyState = () => {
    if (isLoading) {
      return <TransactionListSkeletons />;
    }

    if (isError) {
      return <EmptyState error={error} />;
    }

    if (transactions.length <= 0) {
      return (
        <EmptyStateTransactions
          title={formatMessage({
            defaultMessage: "0 Transactions",
            description: "Zero state for history results",
          })}
          subtitle={formatMessage({
            defaultMessage: "This is where you'll see your transactions",
            description: "Message to user",
          })}
        />
      );
    }

    return null;
  };

  return (
    <VStack align="stretch">
      <TransactionFiltersSection
        show={expandFilters}
        onClickFilterButton={onClickFilterButton}
        onSelect={toggleTxnType}
        filters={types}
      />
      <Box padding="17px">
        {renderEmptyState() ?? (
          <>
            <TransactionListGroupedByDay transactions={transactions} accountId={accountId} />
            <HStack justify="space-between" px={4} mt={4} role="navigation">
              <Box w={100}>
                <Button
                  variant="outline"
                  size="sm"
                  colorScheme="gray"
                  leftIcon={<ChevronLeftIcon />}
                  onClick={goToPreviousPage}
                  isDisabled={!hasPreviousPage || isLoading}
                >
                  <FormattedMessage defaultMessage="Previous" description="Button label" />
                </Button>
              </Box>

              <Box color="secondaryText" fontSize="sm" role="status" aria-label={pageInfo}>
                {pageInfo}
              </Box>

              <Box w={100}>
                <Button
                  variant="outline"
                  size="sm"
                  colorScheme="gray"
                  rightIcon={<ChevronRightIcon />}
                  onClick={goToNextPage}
                  isDisabled={!hasNextPage || isLoading}
                >
                  <FormattedMessage defaultMessage="Next" description="Button label" />
                </Button>
              </Box>
            </HStack>
          </>
        )}
      </Box>
    </VStack>
  );
}
